import { Component } from './base';
import type { Reaction } from '../state/types';

import 'emoji-picker-element';

type EmojiDetail = {
    unicode: string;
    name?: string;
};

export class ReactionPickerComponent extends Component {
    private picker: any;
    private messageId: string | null = null;
    private onEmojiSelected: ((emoji: string) => void) | null = null;

    constructor(element: HTMLElement) {
        super(element);
        this.createPicker();
    }

    private createPicker(): void {
        this.picker = document.createElement('emoji-picker');
        this.picker.classList.add('reaction-picker');
        this.picker.style.display = 'none';
        this.picker.style.position = 'fixed';
        
        this.picker.addEventListener('emoji-click', (event: CustomEvent) => {
            const detail = event.detail as EmojiDetail;
            if (detail?.unicode && this.onEmojiSelected) {
                this.onEmojiSelected(detail.unicode);
                this.hide();
            }
        });

        document.addEventListener('click', (e) => {
            const target = e.target as Node;
            if (!this.picker.contains(target) && !this.isReactionTrigger(target)) {
                this.hide();
            }
        });

        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                this.hide();
            }
        });

        document.body.appendChild(this.picker);
    }

    private isReactionTrigger(target: Node): boolean {
        let current: Node | null = target;
        while (current) {
            if (current instanceof HTMLElement && current.classList.contains('reaction-trigger')) {
                return true;
            }
            current = current.parentNode;
        }
        return false;
    }

    show(messageId: string, x: number, y: number, onEmojiSelected: (emoji: string) => void): void {
        this.messageId = messageId;
        this.onEmojiSelected = onEmojiSelected;
        
        this.picker.style.display = 'block';
        
        const pickerRect = this.picker.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        
        let left = x;
        let top = y;
        
        if (left + pickerRect.width > viewportWidth - 16) {
            left = viewportWidth - pickerRect.width - 16;
        }
        if (left < 16) {
            left = 16;
        }
        
        if (top + pickerRect.height > viewportHeight - 16) {
            top = y - pickerRect.height;
        }
        if (top < 16) {
            top = 16;
        }
        
        this.picker.style.left = `${left}px`;
        this.picker.style.top = `${top}px`;
        this.picker.style.zIndex = '10000';
    }

    hide(): void {
        this.picker.style.display = 'none';
        this.messageId = null;
        this.onEmojiSelected = null;
    }

    isVisible(): boolean {
        return this.picker.style.display !== 'none';
    }

    getMessageId(): string | null {
        return this.messageId;
    }

    destroy(): void {
        super.destroy();
        this.hide();
        this.picker.remove();
    }
}

export function renderReactionBubble(reactions: Reaction[] | undefined, messageRole: 'user' | 'assistant'): string {
    if (!reactions || reactions.length === 0) {
        return '';
    }

    const bubbles: string[] = [];
    
    for (const reaction of reactions) {
        if (reaction.from_role === 'user') {
            bubbles.push(`<span class="reaction-bubble reaction-bubble-user" title="Your reaction">${reaction.emoji}</span>`);
        } else {
            bubbles.push(`<span class="reaction-bubble reaction-bubble-assistant" title="AI reaction">${reaction.emoji}</span>`);
        }
    }

    return bubbles.length > 0 ? `<div class="reaction-bubbles">${bubbles.join('')}</div>` : '';
}

export function getShortId(messageId: string): string {
    return 'msg_' + messageId.replace(/-/g, '').substring(0, 8);
}